import React, { Fragment, useRef, useState } from 'react';
import { Box, Button, Card, Flex, Heading, Text } from 'rebass';
import { Link } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Mutation, withApollo } from 'react-apollo';
import { withToastManager } from 'react-toast-notifications';
import gql from 'graphql-tag';
import moment from 'moment';
import styled from 'styled-components';
import Modal, { BaseModalBackground } from 'styled-react-modal';

import TextInput, { Input } from '../components/TextInput';
import FormField from '../components/FormField';
import Header from '../components/Header';
import withAnalytics from '../lib/withAnalytics';
import { CURRENT_USER, GENERATE_CIRCLE_SO_CALLBACK, GET_ANALYTICS_ID } from '../graphql';
import { useMount } from '../utils/hooks/lifecycle/useMount';
import LoadingScreen from '../components/LoadingScreen';
import FullLayout2 from '../components/FullLayout';
import SpinLoader from '../components/SpinLoader';
import { LOCAL_STORAGE_BASELINE_KEY } from './baselineSurvey/BaselineSurvey';
import { ReferralProgramPopup } from '../components/ReferralProgramPopup';
import { isToggleActive } from '../components/featureToggle/toggles';
import { useEffect } from 'react';
import { getFbclid, getFbp, getGa, getGclAw } from '../utils/fbclid';

const schema = Yup.object().shape({
  email: Yup.string()
    .email('invalid email address')
    .required('Required'),
  password: Yup.string()
    .trim()
    .required(),
});

const LOGIN = gql`
  mutation Login($email: String!, $password: String!, $otp: String, $fbclid: String, $fbp: String, $ga_client_id: String, $gclid: String) {
    login(email: $email, password: $password, otp: $otp, fbclid: $fbclid, fbp: $fbp, ga_client_id: $ga_client_id, gclid: $gclid) {
      token
      user {
        id
        accountLocked
      }
    }
  }
`;

const LOGIN_SMS = gql`
  mutation loginBySms($email: String!, $otp: String, $fbclid: String, $fbp: String, $ga_client_id: String, $gclid: String) {
    loginBySms(email: $email, otp: $otp, fbclid: $fbclid, fbp: $fbp, ga_client_id: $ga_client_id, gclid: $gclid) {
      token
      user {
        id
        accountLocked
      }
    }
  }
`;

const GENERATE_LOGIN_SMS = gql`
  mutation generateLoginSms($email: String!) {
    generateLoginSms(email: $email) {
      ok
    }
  }
`;

const RESEND_LOGIN_SMS = gql`
  mutation resendSmsLogin($email: String!) {
    resendSmsLogin(email: $email) {
      ok
    }
  }
`;

const AB_FLOW = gql`
  mutation abFlow($email: String!) {
    abFlow(email: $email)
  }
`;

const SEND_BY_EMAIL = gql`
  mutation sendOtpCodeViaEmail($email: String!) {
    sendOtpCodeViaEmail(email: $email) {
      ok
    }
  }
`;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Container = styled(Box)`
  max-width: 1024px;
  width: 100%;
  margin: 0 auto;
`;

const CardContainer = styled(Flex)`
  max-width: 400px;
`;

const StyledLink = styled(Link)`
  font-size: 14px;
  color: #999;
  text-align: right;
`;

const OtpInner = ({ mutate, loading, sendByEmail }) => {
  const [value, setValue] = useState();

  return (
    <Flex mb={4} flexDirection="column">
      <Box
        px={2}
        style={{
          width: '100%',
        }}
      >
        {!loading ? (
          <>
            <Heading textAlign="center" mb={3}>
              We sent you an SMS code
            </Heading>
          </>
        ) : (
          <Text>Verifying...</Text>
        )}
      </Box>
      {loading ? (
        <Flex
          px={2}
          flexDirection="column"
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '40px',
          }}
        >
          <SpinLoader />
        </Flex>
      ) : (
        <Flex px={2} flexDirection="column">
          <Text mb={2} textAlign="center" mt={3} style={{ fontWeight: 600 }}>
            Enter your code here
          </Text>
          <Flex justifyContent="center" alignItems="center" flexDirection="column">
            <Input
              style={{
                maxWidth: '250px',
                fontSize: '28px',
                letterSpacing: '16px',
                textAlign: 'center',
              }}
              placeholder="••••••"
              autoComplete="one-time-code"
              onChange={(r) => {
                setValue(r.target.value);
                if (r.target.value.length === 6) {
                  mutate(r.target.value);
                }
              }}
              autoFocus
              value={value}
            />

            <Text onClick={() => sendByEmail()} style={{ textDecoration: 'underline', cursor: 'pointer', textAlign: 'center' }} mt={4}>
              Send new code by email
            </Text>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

const RequestCodeButton = ({ lastRequest, onResend }) => {
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(
    () => {
      if (!lastRequest) return;

      const updateRemaining = () => {
        const elapsed = moment().diff(lastRequest, 'seconds');
        const remaining = 30 - elapsed;
        setRemainingSeconds(Math.max(remaining, 0));
      };

      updateRemaining();
      const interval = setInterval(updateRemaining, 1000);

      return () => clearInterval(interval);
    },
    [lastRequest],
  );

  const isDisabled = remainingSeconds > 0;

  return (
    <Button type="button" disabled={isDisabled} onClick={onResend} variant={'outline'} style={{ width: 'fit-content', alignSelf: 'center' }}>
      {isDisabled ? `Resend code (available in ${remainingSeconds}s)` : 'Resend code'}
    </Button>
  );
};
const SmsOtpInner = ({ mutate, loading, email, forcePwd, lastRequest, onRequestNew }) => {
  const [value, setValue] = useState();
  const [showFaq, setShowFaq] = useState(false);
  return (
    <Flex mb={4} flexDirection="column">
      <Box
        px={2}
        style={{
          width: '100%',
        }}
      >
        {!loading ? (
          <>
            <Heading textAlign="center" mb={3}>
              We sent you an SMS code
            </Heading>
          </>
        ) : (
          <Text>Verifying...</Text>
        )}
        <Text textAlign={'center'}>
          If an account exists, a verification code has been sent by SMS to the phone number on file.
          <br />A code expires in 3 minutes.
        </Text>
      </Box>
      {loading ? (
        <Flex
          px={2}
          flexDirection="column"
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '40px',
          }}
        >
          <SpinLoader />
        </Flex>
      ) : (
        <Flex px={2} flexDirection="column">
          <Text mb={2} textAlign="center" mt={3} style={{ fontWeight: 600 }}>
            Enter your code here
          </Text>
          <Flex justifyContent="center" alignItems="center" flexDirection="column">
            <Input
              style={{
                maxWidth: '250px',
                fontSize: '28px',
                letterSpacing: '16px',
                textAlign: 'center',
              }}
              placeholder="••••••"
              autoComplete="one-time-code"
              onChange={(r) => {
                setValue(r.target.value);
                if (r.target.value.length === 6) {
                  mutate(r.target.value);
                }
              }}
              autoFocus
              value={value}
            />
          </Flex>
          <Text
            mt={2}
            textAlign={'center'}
            style={{ color: '#ffa844ff', fontSize: '14px', cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}
            onClick={() => {
              setShowFaq(true);
            }}
            mb={3}
          >{`I'm not receiving any codes >`}</Text>
          {/* <Text mb={3} textAlign="center" mt={3} style={{ fontWeight: 300 }}>
            For security reasons, we may limit the number of requests.
          </Text> */}
          <RequestCodeButton lastRequest={lastRequest} onResend={onRequestNew} />
          <Text
            mt={4}
            textAlign={'center'}
            style={{ color: '#44A5FF', fontSize: '14px', cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              forcePwd();
            }}
          >{`Sign-in with email and password >`}</Text>
        </Flex>
      )}
      <Modal isOpen={!!showFaq} onBackgroundClick={() => setShowFaq(false)} onEscapeKeydown={() => setShowFaq(false)}>
        {/* <SpecialModalBackground> */}
        <Box
          style={{
            margin: '0 auto',
            alignSelf: 'center',
            borderRadius: '20px',
            position: 'relative',
            backgroundColor: 'white',
            padding: '24px',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            maxWidth: '80vw',
            width: '400px',
          }}
        >
          <Heading mb={3} textAlign={'center'}>
            Why am I not receiving any codes?
          </Heading>
          <Text mb={3} textAlign="center">
            Here are a few reasons why you may not receive any codes by SMS:
          </Text>
          <Text fontWeight={500}>You never enabled the feature on your account</Text>
          <Text ml={2} style={{ fontSize: '13px', fontWeight: 300 }}>
            You need to first log in using email and password. If you never rejected the feature, a banner will be displayed at the top of the screen so you can enable it.
          </Text>

          <Text mt={3} fontWeight={500}>
            You changed your phone number after enabling the feature
          </Text>
          <Text ml={2} style={{ fontSize: '13px', fontWeight: 300 }}>
            To protect your account, after changing your phone number, you'll have to re-enable the feature.
          </Text>

          <Text mt={3} fontWeight={500}>
            You requested a code recently
          </Text>
          <Text ml={2} style={{ fontSize: '13px', fontWeight: 300 }}>
            Codes are valid for 3 minutes. New codes are not sent during this timeframe.
          </Text>

          <Text mt={3} fontWeight={500}>
            You entered too many incorrect codes
          </Text>
          <Text ml={2} style={{ fontSize: '13px', fontWeight: 300 }}>
            For security measures, the feature is temporarily turned off on your account if too many incorrect codes have been attempted.
          </Text>

          <Flex justifyContent="center" flexDirection={'row'} width={'100%'}>
            <Button variant={'primary'} onClick={() => setShowFaq(false)} mt={4}>
              Close
            </Button>
          </Flex>
        </Box>
        {/* </SpecialModalBackground> */}
      </Modal>
    </Flex>
  );
};

const Login = ({ toastManager, analytics, client, history, location }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [mustEnterOtp, setMustEnterOtp] = useState(false);
  const [storedData, setStoredData] = useState(null);
  const [signinWithSms, setSigninWithSms] = useState(false);
  const [lastRequest, setLastRequest] = useState(null);

  useMount(() => {
    analytics.page();
    localStorage.removeItem('initialToken');
    localStorage.removeItem(LOCAL_STORAGE_BASELINE_KEY);
  });
  if (!!isToggleActive('SMS_LOGIN')) {
    return <LoginNew toastManager={toastManager} analytics={analytics} client={client} history={history} location={location} />;
  }

  const sendByEmail = async () => {
    const aa = await client.mutate({
      mutation: SEND_BY_EMAIL,
      variables: {
        email: storedData.email,
      },
    });

    if (aa && aa.data && aa.data.sendOtpCodeViaEmail) {
      toastManager.add('Email Sent', {
        appearance: 'success',
        autoDismiss: true,
      });
    } else {
      toastManager.add('An error occured', {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  };

  const handleSubmit = async (values, login, actions) => {
    setIsLoading(true);
    try {
      if (!values.otp) {
        setStoredData({ ...values });
      }

      let fbclid = getFbclid();
      let fbp = getFbp();
      let ga_client_id = getGa();
      let gclid = getGclAw();
      const { data } = await login({
        variables: {
          ...values,
          fbclid,
          fbp,
          ga_client_id,
          gclid,
        },
      });

      // console.log({ error });
      const attemptedRoute = localStorage.getItem('route');
      localStorage.setItem('token', data.login.token);

      const aa = await client.query({
        query: GET_ANALYTICS_ID,
        fetchPolicy: 'network-only',
      });

      if (aa && aa.data && aa.data.getAnalyticsId) {
        analytics.Alias(aa.data.getAnalyticsId);
        try {
          const pt = await client.query({
            query: CURRENT_USER,
            fetchPolicy: 'network-only',
          });
          let age;
          let city;
          let state;
          let zip;
          let departmentId;
          let hashimoto;
          try {
            age = moment().diff(pt.data.currentUser.dob, 'years');
            if (pt.data.currentUser.address) {
              city = pt.data.currentUser.address.city;
              zip = pt.data.currentUser.address.zip;
              state = pt.data.currentUser.address.state;
            }
            if (pt.data.currentUser.department) {
              departmentId = pt.data.currentUser.department.departmentId;
            }
            if (pt.data.currentUser.diagnoseFull) {
              hashimoto = pt.data.currentUser.diagnoseFull.hasHashi;
            }
          } catch (error) {}

          analytics.Identify(aa.data.getAnalyticsId, {
            age,
            gender: pt.data.currentUser.gender,
            // city,
            // zip,
            state,
            departmentId,
            diagnosed: pt.data.currentUser.isDiagnosed,
            hashimoto: !!hashimoto,
            // dob: pt.data.currentUser.dob,
            // phone: pt.data.currentUser.phone
          });
        } catch (error) {
          // console.log('err id', error)
        }
      }

      if (location && location.search && location.search.indexOf('community') > -1) {
        const circleSo = await client.mutate({
          mutation: GENERATE_CIRCLE_SO_CALLBACK,
        });

        if (circleSo && circleSo.data && circleSo.data.generateCircleSoCallback) {
          if (!!circleSo.data.generateCircleSoCallback.callback) {
            window.open(circleSo.data.generateCircleSoCallback.callback, '_blank');
          } else if (!!circleSo.data.generateCircleSoCallback.mustAcceptTerms) {
            history.push({
              pathname: '/community-terms',
              state: { sameWindow: true },
            });
            return;
          }
        }
      }

      //

      window.sessionStorage.removeItem('wmi');
      window.localStorage.removeItem('wm-d');
      window.localStorage.removeItem('wm-df');
      const finalRoute = attemptedRoute ? (attemptedRoute.indexOf('/scheduler') > -1 ? '/scheduler' : attemptedRoute.indexOf('/schedule-split') > -1 ? '/schedule-split' : attemptedRoute) : '/';
      setStoredData(null);
      history.push(finalRoute);
    } catch (error) {
      setIsLoading(false);

      if (!error.graphQLErrors) {
        return;
      }

      error.graphQLErrors.forEach((error) => {
        if (error.message.toLowerCase() === 'error: otp req') {
          setMustEnterOtp(true);
          return;
        }
        if (error.message.toLowerCase() === 'error: account locked') {
          history.push({
            pathname: '/account-locked',
            state: { email: values.email },
          });
        } else {
          toastManager.add(error.message, {
            appearance: 'error',
            autoDismiss: true,
          });
        }
      });
    }
  };

  const handleSubmitSms = async (values, login, actions) => {
    setIsLoading(true);
    try {
      if (!values.otp) {
        setStoredData({ ...values });
      }

      let fbclid = getFbclid();
      let fbp = getFbp();
      let ga_client_id = getGa();
      let gclid = getGclAw();
      const { data } = await login({
        variables: {
          ...values,
          fbclid,
          fbp,
          ga_client_id,
          gclid,
        },
      });

      // console.log({ error });
      const attemptedRoute = localStorage.getItem('route');
      localStorage.setItem('token', data.loginBySms.token);

      const aa = await client.query({
        query: GET_ANALYTICS_ID,
        fetchPolicy: 'network-only',
      });

      if (aa && aa.data && aa.data.getAnalyticsId) {
        analytics.Alias(aa.data.getAnalyticsId);
        try {
          const pt = await client.query({
            query: CURRENT_USER,
            fetchPolicy: 'network-only',
          });
          let age;
          let city;
          let state;
          let zip;
          let departmentId;
          let hashimoto;
          try {
            age = moment().diff(pt.data.currentUser.dob, 'years');
            if (pt.data.currentUser.address) {
              city = pt.data.currentUser.address.city;
              zip = pt.data.currentUser.address.zip;
              state = pt.data.currentUser.address.state;
            }
            if (pt.data.currentUser.department) {
              departmentId = pt.data.currentUser.department.departmentId;
            }
            if (pt.data.currentUser.diagnoseFull) {
              hashimoto = pt.data.currentUser.diagnoseFull.hasHashi;
            }
          } catch (error) {}

          analytics.Identify(aa.data.getAnalyticsId, {
            age,
            gender: pt.data.currentUser.gender,
            // city,
            // zip,
            state,
            departmentId,
            diagnosed: pt.data.currentUser.isDiagnosed,
            hashimoto: !!hashimoto,
            // dob: pt.data.currentUser.dob,
            // phone: pt.data.currentUser.phone
          });
        } catch (error) {
          // console.log('err id', error)
        }
      }

      if (location && location.search && location.search.indexOf('community') > -1) {
        const circleSo = await client.mutate({
          mutation: GENERATE_CIRCLE_SO_CALLBACK,
        });

        if (circleSo && circleSo.data && circleSo.data.generateCircleSoCallback) {
          if (!!circleSo.data.generateCircleSoCallback.callback) {
            window.open(circleSo.data.generateCircleSoCallback.callback, '_blank');
          } else if (!!circleSo.data.generateCircleSoCallback.mustAcceptTerms) {
            history.push({
              pathname: '/community-terms',
              state: { sameWindow: true },
            });
            return;
          }
        }
      }

      //

      window.sessionStorage.removeItem('wmi');
      window.localStorage.removeItem('wm-d');
      window.localStorage.removeItem('wm-df');
      const finalRoute = attemptedRoute ? (attemptedRoute.indexOf('/scheduler') > -1 ? '/scheduler' : attemptedRoute.indexOf('/schedule-split') > -1 ? '/schedule-split' : attemptedRoute) : '/';
      setStoredData(null);
      history.push(finalRoute);
    } catch (error) {
      setIsLoading(false);

      if (!error.graphQLErrors) {
        return;
      }

      error.graphQLErrors.forEach((error) => {
        if (error.message.toLowerCase() === 'error: otp req') {
          setMustEnterOtp(true);
          return;
        }
        if (error.message.toLowerCase() === 'error: account locked') {
          history.push({
            pathname: '/account-locked',
            state: { email: values.email },
          });
        } else {
          toastManager.add(error.message, {
            appearance: 'error',
            autoDismiss: true,
          });
        }
      });
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <FullLayout2 style={{ overflow: 'auto', zIndex: 10 }}>
      <Fragment>
        <Container>
          <Flex flex={1}>
            <Header dark={false} />
          </Flex>
          <Flex flex={1} alignItems="center" justifyContent="center">
            <CardContainer flex={1} px={3}>
              <Card p={4}>
                <Flex flexDirection="column">
                  {!signinWithSms && (
                    <Box mb={4}>
                      <Heading textAlign="center" mb={3}>
                        Login To Your Account
                      </Heading>
                      <Text textAlign="center">
                        Don't have an account?{' '}
                        <Link
                          to={{
                            pathname: '/prequalification',
                            state: { continueWith: location && location.search && location.search.indexOf('community') > -1 ? '/community-terms' : null },
                          }}
                          id="link_signup"
                        >
                          Sign Up!
                        </Link>
                      </Text>
                    </Box>
                  )}

                  <Mutation mutation={LOGIN}>
                    {(login, { loading }) => {
                      return (
                        <Fragment>
                          <Formik
                            initialValues={{
                              email: '',
                              password: '',
                            }}
                            validationSchema={schema}
                            onSubmit={(values, actions) => handleSubmit(values, login, actions)}
                            render={({ touched, errors, isValid, values }) => {
                              return (
                                <>
                                  {!!isToggleActive('SMS_LOGIN') && signinWithSms ? (
                                    <Mutation mutation={LOGIN_SMS}>
                                      {(loginSms, { loading: loadingSms }) => (
                                        <Mutation mutation={RESEND_LOGIN_SMS}>
                                          {(resendSms, { loading: loadingResendSms }) => (
                                            <SmsOtpInner
                                              lastRequest={lastRequest}
                                              mutate={(otp) => {
                                                handleSubmitSms({ email: values.email, otp }, loginSms, null);
                                              }}
                                              loading={!!loadingSms || loading || loadingResendSms}
                                              email={values.email}
                                              onRequestNew={() => {
                                                resendSms({
                                                  variables: {
                                                    email: values.email,
                                                  },
                                                });
                                                setLastRequest(moment());
                                              }}
                                              goBack={() => setSigninWithSms(false)}
                                            />
                                          )}
                                        </Mutation>
                                      )}
                                    </Mutation>
                                  ) : (
                                    <>
                                      {!mustEnterOtp ? (
                                        <Form>
                                          <FormField mb={3} error={touched.email && errors.email}>
                                            <TextInput placeholder="Your email address" type="email" name="email" id="input_email" />
                                          </FormField>

                                          <FormField mb={4} error={touched.phone && errors.phone}>
                                            <TextInput placeholder="Password" type="password" name="password" id="input_password" />
                                            <StyledLink to="/recovery" id="link_forgotPassword">
                                              Forgot Password?
                                            </StyledLink>
                                          </FormField>
                                          <Flex alignItems="center" flexDirection="column">
                                            <Button disabled={!isValid || loading || isLoading} variant="primary" width={1 / 2} type="submit" id="btn_login">
                                              Login
                                            </Button>
                                            {isToggleActive('SMS_LOGIN') &&
                                              values.email &&
                                              !!emailRegex.test(values.email) && (
                                                <Mutation
                                                  mutation={GENERATE_LOGIN_SMS}
                                                  onCompleted={(e) => {
                                                    setLastRequest(moment());
                                                    if (e && e.generateLoginSms) {
                                                      setSigninWithSms(true);
                                                    }
                                                  }}
                                                  variables={{ email: values.email }}
                                                >
                                                  {(generateSms, { loading: loadingSms }) => {
                                                    return (
                                                      <Button
                                                        onClick={() => {
                                                          generateSms();
                                                        }}
                                                        mt={4}
                                                        disabled={!emailRegex.test(values.email) || loading || isLoading || loadingSms}
                                                        variant="outline"
                                                        style={{
                                                          width: 'fit-content',
                                                        }}
                                                        flex={'initial'}
                                                        type="button"
                                                        id="btn_login2"
                                                      >
                                                        Login with SMS code
                                                      </Button>
                                                    );
                                                  }}
                                                </Mutation>
                                              )}
                                          </Flex>
                                        </Form>
                                      ) : (
                                        <OtpInner
                                          mutate={(otp) => {
                                            handleSubmit({ ...storedData, otp }, login, null);
                                          }}
                                          sendByEmail={sendByEmail}
                                          loading={!!isLoading || !!loading}
                                        />
                                      )}
                                    </>
                                  )}
                                </>
                              );
                            }}
                          />
                        </Fragment>
                      );
                    }}
                  </Mutation>
                </Flex>
              </Card>
            </CardContainer>
          </Flex>
        </Container>
      </Fragment>
    </FullLayout2>
  );
};

const SmsWrap = ({ email, generate, loadingAwaitSms, setLastRequest, lastRequest, handleSubmitSms, setAbFlow }) => {
  useEffect(() => {
    generate();
  }, []);

  return (
    <Mutation mutation={LOGIN_SMS}>
      {(loginSms, { loading: loadingSms }) => (
        <Mutation mutation={RESEND_LOGIN_SMS}>
          {(resendSms, { loading: loadingResendSms }) => (
            <SmsOtpInner
              lastRequest={lastRequest}
              mutate={(otp) => {
                handleSubmitSms({ email: email, otp }, loginSms, null);
              }}
              loading={!!loadingAwaitSms || !!loadingSms || loadingResendSms}
              email={email}
              onRequestNew={() => {
                resendSms({
                  variables: {
                    email: email,
                  },
                });
                setLastRequest(moment());
              }}
              forcePwd={() => {
                setAbFlow('A');
              }}
            />
          )}
        </Mutation>
      )}
    </Mutation>
  );
};

const LoginNew = ({ toastManager, analytics, client, history, location }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [abFlow, setAbFlow] = useState('');
  const [mustEnterOtp, setMustEnterOtp] = useState(false);
  const [storedData, setStoredData] = useState(null);
  const [signinWithSms, setSigninWithSms] = useState(false);
  const [lastRequest, setLastRequest] = useState(null);
  const pwdRef = useRef(null);

  useMount(() => {
    analytics.page();
    localStorage.removeItem('initialToken');
    localStorage.removeItem(LOCAL_STORAGE_BASELINE_KEY);
  });

  useEffect(()=>{
if (abFlow && abFlow==='A' && pwdRef && pwdRef.current) {
      pwdRef.current.focus();
    }
  },[abFlow])

  const sendByEmail = async () => {
    const aa = await client.mutate({
      mutation: SEND_BY_EMAIL,
      variables: {
        email: storedData.email,
      },
    });

    if (aa && aa.data && aa.data.sendOtpCodeViaEmail) {
      toastManager.add('Email Sent', {
        appearance: 'success',
        autoDismiss: true,
      });
    } else {
      toastManager.add('An error occured', {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  };

  const handleSubmit = async (values, login, actions) => {
    setIsLoading(true);
    try {
      if (!values.otp) {
        setStoredData({ ...values });
      }
      let fbclid = getFbclid();
      let fbp = getFbp();
      let ga_client_id = getGa();
      let gclid = getGclAw();
      const { data } = await login({
        variables: {
          ...values,
          fbclid,
          fbp,
          ga_client_id,
          gclid,
        },
      });

      const attemptedRoute = localStorage.getItem('route');
      localStorage.setItem('token', data.login.token);

      const aa = await client.query({
        query: GET_ANALYTICS_ID,
        fetchPolicy: 'network-only',
      });

      if (aa && aa.data && aa.data.getAnalyticsId) {
        analytics.Alias(aa.data.getAnalyticsId);
        try {
          const pt = await client.query({
            query: CURRENT_USER,
            fetchPolicy: 'network-only',
          });
          let age;
          let city;
          let state;
          let zip;
          let departmentId;
          let hashimoto;
          try {
            age = moment().diff(pt.data.currentUser.dob, 'years');
            if (pt.data.currentUser.address) {
              city = pt.data.currentUser.address.city;
              zip = pt.data.currentUser.address.zip;
              state = pt.data.currentUser.address.state;
            }
            if (pt.data.currentUser.department) {
              departmentId = pt.data.currentUser.department.departmentId;
            }
            if (pt.data.currentUser.diagnoseFull) {
              hashimoto = pt.data.currentUser.diagnoseFull.hasHashi;
            }
          } catch (error) {}

          analytics.Identify(aa.data.getAnalyticsId, {
            age,
            gender: pt.data.currentUser.gender,
            // city,
            // zip,
            state,
            departmentId,
            diagnosed: pt.data.currentUser.isDiagnosed,
            hashimoto: !!hashimoto,
            // dob: pt.data.currentUser.dob,
            // phone: pt.data.currentUser.phone
          });
        } catch (error) {
          // console.log('err id', error)
        }
      }

      if (location && location.search && location.search.indexOf('community') > -1) {
        const circleSo = await client.mutate({
          mutation: GENERATE_CIRCLE_SO_CALLBACK,
        });

        if (circleSo && circleSo.data && circleSo.data.generateCircleSoCallback) {
          if (!!circleSo.data.generateCircleSoCallback.callback) {
            window.open(circleSo.data.generateCircleSoCallback.callback, '_blank');
          } else if (!!circleSo.data.generateCircleSoCallback.mustAcceptTerms) {
            history.push({
              pathname: '/community-terms',
              state: { sameWindow: true },
            });
            return;
          }
        }
      }

      //

      window.sessionStorage.removeItem('wmi');
      window.localStorage.removeItem('wm-d');
      window.localStorage.removeItem('wm-df');
      const finalRoute = attemptedRoute ? (attemptedRoute.indexOf('/scheduler') > -1 ? '/scheduler' : attemptedRoute.indexOf('/schedule-split') > -1 ? '/schedule-split' : attemptedRoute) : '/';
      // setStoredData(null);
      history.push(finalRoute);
    } catch (error) {
      setIsLoading(false);

      if (!error.graphQLErrors) {
        return;
      }

      error.graphQLErrors.forEach((error) => {
        if (error.message.toLowerCase() === 'error: otp req') {
          setMustEnterOtp(true);
          return;
        }
        if (error.message.toLowerCase() === 'error: account locked') {
          history.push({
            pathname: '/account-locked',
            state: { email: values.email },
          });
        } else {
          toastManager.add(error.message, {
            appearance: 'error',
            autoDismiss: true,
          });
        }
      });
    }
  };

  const handleSubmitSms = async (values, login, actions) => {
    setIsLoading(true);
    try {
      // if (!values.otp) {
      setStoredData({ ...values });
      // }
      let fbclid = getFbclid();
      let fbp = getFbp();
      let ga_client_id = getGa();
      let gclid = getGclAw();
      const { data } = await login({
        variables: {
          ...values,
          fbclid,
          fbp,
          ga_client_id,
          gclid,
        },
      });

      // console.log({ error });
      const attemptedRoute = localStorage.getItem('route');
      localStorage.setItem('token', data.loginBySms.token);

      const aa = await client.query({
        query: GET_ANALYTICS_ID,
        fetchPolicy: 'network-only',
      });

      if (aa && aa.data && aa.data.getAnalyticsId) {
        analytics.Alias(aa.data.getAnalyticsId);
        try {
          const pt = await client.query({
            query: CURRENT_USER,
            fetchPolicy: 'network-only',
          });
          let age;
          let city;
          let state;
          let zip;
          let departmentId;
          let hashimoto;
          try {
            age = moment().diff(pt.data.currentUser.dob, 'years');
            if (pt.data.currentUser.address) {
              city = pt.data.currentUser.address.city;
              zip = pt.data.currentUser.address.zip;
              state = pt.data.currentUser.address.state;
            }
            if (pt.data.currentUser.department) {
              departmentId = pt.data.currentUser.department.departmentId;
            }
            if (pt.data.currentUser.diagnoseFull) {
              hashimoto = pt.data.currentUser.diagnoseFull.hasHashi;
            }
          } catch (error) {}

          analytics.Identify(aa.data.getAnalyticsId, {
            age,
            gender: pt.data.currentUser.gender,
            // city,
            // zip,
            state,
            departmentId,
            diagnosed: pt.data.currentUser.isDiagnosed,
            hashimoto: !!hashimoto,
            // dob: pt.data.currentUser.dob,
            // phone: pt.data.currentUser.phone
          });
        } catch (error) {
          // console.log('err id', error)
        }
      }

      if (location && location.search && location.search.indexOf('community') > -1) {
        const circleSo = await client.mutate({
          mutation: GENERATE_CIRCLE_SO_CALLBACK,
        });

        if (circleSo && circleSo.data && circleSo.data.generateCircleSoCallback) {
          if (!!circleSo.data.generateCircleSoCallback.callback) {
            window.open(circleSo.data.generateCircleSoCallback.callback, '_blank');
          } else if (!!circleSo.data.generateCircleSoCallback.mustAcceptTerms) {
            history.push({
              pathname: '/community-terms',
              state: { sameWindow: true },
            });
            return;
          }
        }
      }

      //

      window.sessionStorage.removeItem('wmi');
      window.localStorage.removeItem('wm-d');
      window.localStorage.removeItem('wm-df');
      const finalRoute = attemptedRoute ? (attemptedRoute.indexOf('/scheduler') > -1 ? '/scheduler' : attemptedRoute.indexOf('/schedule-split') > -1 ? '/schedule-split' : attemptedRoute) : '/';
      // setStoredData(null);
      history.push(finalRoute);
    } catch (error) {
      setIsLoading(false);

      if (!error.graphQLErrors) {
        return;
      }

      error.graphQLErrors.forEach((error) => {
        if (error.message.toLowerCase() === 'error: otp req') {
          setMustEnterOtp(true);
          return;
        }
        if (error.message.toLowerCase() === 'error: account locked') {
          history.push({
            pathname: '/account-locked',
            state: { email: values.email },
          });
        } else {
          toastManager.add(error.message, {
            appearance: 'error',
            autoDismiss: true,
          });
        }
      });
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <FullLayout2 style={{ overflow: 'auto', zIndex: 10 }}>
      <Fragment>
        <Container>
          <Flex flex={1}>
            <Header dark={false} />
          </Flex>
          <Flex flex={1} alignItems="center" justifyContent="center">
            <CardContainer flex={1} px={3}>
              <Card p={4}>
                <Flex flexDirection="column">
                  {!signinWithSms && (
                    <Box mb={4}>
                      <Heading textAlign="center" mb={3}>
                        {!abFlow ? 'Login To Your Account' : abFlow === 'B' ? 'Enter Code' : 'Enter your password'}
                      </Heading>
                      <Text textAlign="center">
                        Don't have an account?{' '}
                        <Link
                          to={{
                            pathname: '/prequalification',
                            state: { continueWith: location && location.search && location.search.indexOf('community') > -1 ? '/community-terms' : null },
                          }}
                          id="link_signup"
                        >
                          Sign Up!
                        </Link>
                      </Text>
                    </Box>
                  )}
                  <Mutation
                    mutation={AB_FLOW}
                    onCompleted={(e) => {
                      if (e && e.abFlow) {
                        setAbFlow(e.abFlow);
                      } else {
                        setAbFlow('');
                      }
                    }}
                  >
                    {(mutateAb, { loading: loadingAb }) => (
                      <Mutation mutation={LOGIN}>
                        {(login, { loading }) => {
                          return (
                            <Fragment>
                              <Formik
                                initialValues={
                                  storedData
                                    ? { ...storedData }
                                    : {
                                        email: '',
                                        password: '',
                                      }
                                }
                                validationSchema={schema}
                                onSubmit={(values, actions) => handleSubmit(values, login, actions)}
                                render={({ touched, errors, isValid, values }) => {
                                  return (
                                    <>
                                      {!!isToggleActive('SMS_LOGIN') && signinWithSms ? (
                                        <Mutation mutation={LOGIN_SMS}>
                                          {(loginSms, { loading: loadingSms }) => (
                                            <Mutation mutation={RESEND_LOGIN_SMS}>
                                              {(resendSms, { loading: loadingResendSms }) => (
                                                <SmsOtpInner
                                                  lastRequest={lastRequest}
                                                  mutate={(otp) => {
                                                    handleSubmitSms({ email: values.email, otp }, loginSms, null);
                                                  }}
                                                  loading={!!loadingSms || loading || loadingResendSms}
                                                  email={values.email}
                                                  onRequestNew={() => {
                                                    resendSms({
                                                      variables: {
                                                        email: values.email,
                                                      },
                                                    });
                                                    setLastRequest(moment());
                                                  }}
                                                  forcePwd={() => {
                                                    setSigninWithSms(false);
                                                    setAbFlow('A');
                                                  }}
                                                />
                                              )}
                                            </Mutation>
                                          )}
                                        </Mutation>
                                      ) : (
                                        <>
                                          {!mustEnterOtp ? (
                                            <>
                                              {!abFlow ? (
                                                <Form>
                                                  <>
                                                    <FormField mb={3} error={touched.email && errors.email}>
                                                      <TextInput
                                                        placeholder="Your email address"
                                                        type="email"
                                                        name="email"
                                                        autoFocus
                                                        id="input_email"
                                                        onEnter={() => {
                                                          if (values.email && emailRegex.test(values.email) && !loadingAb && !loading && !isLoading) {
                                                            mutateAb({ variables: { email: values.email } });
                                                          }
                                                        }}
                                                      />
                                                    </FormField>

                                                    <Flex alignItems="center" flexDirection="column">
                                                      <Button
                                                        mt={4}
                                                        disabled={loadingAb || !values.email || !emailRegex.test(values.email) || loading || isLoading}
                                                        variant="primary"
                                                        width={1 / 2}
                                                        type="button"
                                                        id="btn_cont"
                                                        onClick={() => mutateAb({ variables: { email: values.email } })}
                                                      >
                                                        Continue
                                                      </Button>
                                                    </Flex>
                                                  </>
                                                </Form>
                                              ) : (
                                                <>
                                                  {abFlow === 'B' ? (
                                                    <Form>
                                                      <>
                                                        <Mutation
                                                          mutation={GENERATE_LOGIN_SMS}
                                                          onCompleted={(e) => {
                                                            setLastRequest(moment());
                                                            if (e && e.generateLoginSms) {
                                                              setSigninWithSms(true);
                                                            }
                                                          }}
                                                          variables={{ email: values.email }}
                                                        >
                                                          {(generateSms, { loading: loadingSms }) => {
                                                            return (
                                                              <SmsWrap
                                                                handleSubmitSms={handleSubmitSms}
                                                                setLastRequest={setLastRequest}
                                                                lastRequest={lastRequest}
                                                                email={values.email}
                                                                generate={generateSms}
                                                                loadingAwaitSms={loadingSms}
                                                                setAbFlow={(val) => {
                                                                  setAbFlow(val);
                                                                }}
                                                              />
                                                            );
                                                          }}
                                                        </Mutation>
                                                      </>
                                                    </Form>
                                                  ) : (
                                                    <Form>
                                                      <>
                                                        <Flex justifyContent={'space-between'} alignItems={'center'} mb={3}>
                                                          <Text flex={1}>{values.email}</Text>
                                                          <Box onClick={() => setAbFlow('')} style={{ cursor: 'pointer' }}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                              <path
                                                                d="M14.06 9.02L14.98 9.94L5.92 19H5V18.08L14.06 9.02ZM17.66 3C17.41 3 17.15 3.1 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04C20.8027 6.94749 20.8763 6.8376 20.9264 6.71662C20.9766 6.59565 21.0024 6.46597 21.0024 6.335C21.0024 6.20403 20.9766 6.07435 20.9264 5.95338C20.8763 5.8324 20.8027 5.72251 20.71 5.63L18.37 3.29C18.17 3.09 17.92 3 17.66 3ZM14.06 6.19L3 17.25V21H6.75L17.81 9.94L14.06 6.19Z"
                                                                fill="#354E79"
                                                              />
                                                            </svg>
                                                          </Box>
                                                        </Flex>
                                                        <FormField mb={4} error={touched.phone && errors.phone}>
                                                          <TextInput subref={pwdRef} placeholder="Password" type="password" name="password" id="input_password" />
                                                          <StyledLink to="/recovery" id="link_forgotPassword">
                                                            Forgot Password?
                                                          </StyledLink>
                                                        </FormField>

                                                        <Flex alignItems="center" flexDirection="column">
                                                          <Button mt={4} disabled={!isValid || loading || isLoading} variant="primary" width={1 / 2} type="submit" id="btn_login">
                                                            Login
                                                          </Button>
                                                        </Flex>
                                                      </>
                                                    </Form>
                                                  )}
                                                </>
                                              )}

                                              {/* <Flex alignItems="center" flexDirection="column">
                                            <Button disabled={!isValid || loading || isLoading} variant="primary" width={1 / 2} type="submit" id="btn_login">
                                              Login
                                            </Button>
                                            {isToggleActive('SMS_LOGIN') &&
                                              values.email &&
                                              !!emailRegex.test(values.email) && (
                                                <Mutation
                                                  mutation={GENERATE_LOGIN_SMS}
                                                  onCompleted={(e) => {
                                                    setLastRequest(moment());
                                                    if (e && e.generateLoginSms) {
                                                      setSigninWithSms(true);
                                                    }
                                                  }}
                                                  variables={{ email: values.email }}
                                                >
                                                  {(generateSms, { loading: loadingSms }) => {
                                                    return (
                                                      <Button
                                                        onClick={() => {
                                                          generateSms();
                                                        }}
                                                        mt={4}
                                                        disabled={!emailRegex.test(values.email) || loading || isLoading || loadingSms}
                                                        variant="outline"
                                                        style={{
                                                          width: 'fit-content',
                                                        }}
                                                        flex={'initial'}
                                                        type="button"
                                                        id="btn_login2"
                                                      >
                                                        Login with SMS code
                                                      </Button>
                                                    );
                                                  }}
                                                </Mutation>
                                              )}
                                          </Flex> */}
                                            </>
                                          ) : (
                                            <OtpInner
                                              mutate={(otp) => {
                                                handleSubmit({ ...storedData, otp }, login, null);
                                              }}
                                              sendByEmail={sendByEmail}
                                              loading={!!isLoading || !!loading}
                                            />
                                          )}
                                        </>
                                      )}
                                    </>
                                  );
                                }}
                              />
                            </Fragment>
                          );
                        }}
                      </Mutation>
                    )}
                  </Mutation>
                </Flex>
              </Card>
            </CardContainer>
          </Flex>
        </Container>
      </Fragment>
    </FullLayout2>
  );
};

export default withApollo(withAnalytics(withToastManager(Login)));
